import classNames from "classnames";
import { motion } from "framer-motion";
import compact from "lodash/compact";
import map from "lodash/map";
import noop from "lodash/noop";
import React, {
  FunctionComponent,
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import { useIntl } from "react-intl";
import { v1 as uuid } from "uuid";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { OrganizedMode } from "../../../../states/customization";
import { getNoCoverDefinitions } from "../../../../utils/customization/cover";
import { isTouchDevice } from "../../../../utils/customization/device";
import Page from "../Page";
import PagePresentation from "../PagePresentation";
import DraggablePage from "./DraggablePage";
import RemovablePage from "./RemovablePage";
import "./style.scss";

const motionItemAnimate = { opacity: 1, y: 0 };
const motionItemExit = { opacity: 0, y: -20 };
const motionItemInitial = { opacity: 0, y: 20 };
const motionItemSpring = {
  type: "spring",
  damping: 10,
  stiffness: 100,
};

interface Props {
  children?: ReactElement;
  organizeMode: OrganizedMode;
  onItemSelected?: (values: string[]) => void;
  ppmm: number;
}

const EditablePageList: FunctionComponent<Props> = ({
  children,
  onItemSelected,
  organizeMode,
  ppmm,
}) => {
  const intl = useIntl();
  const { content, configuration, dispatch, template } = useCustomizationContext();

  const pageListRef = useRef<HTMLDivElement>(null);

  const pageListElement = pageListRef.current as HTMLElement;
  const [pageTargetedByDrag, setPageTargetedByDrag] = useState<{
    pageKey: string;
    position: "before" | "after";
  } | null>(null);
  const [draggedPage, setDraggedPage] = useState<{
    pageKey: string;
    element: HTMLDivElement;
  } | null>(null);

  const noCoverPageDefinitions = getNoCoverDefinitions(template.pageDefinitions);
  const noCoverPageDefinitionsKeys = noCoverPageDefinitions.map((pageDefinition) => {
    return pageDefinition.key;
  });
  const contentPages = content.pages.filter((page) => {
    return noCoverPageDefinitionsKeys.includes(page.pageDefinitionKey);
  });

  const setElementPosition = useCallback(
    (movableElement: HTMLElement, event: { pageX: number; pageY: number }) => {
      if (!movableElement || !pageListElement) {
        return;
      }
      const scrollTop = pageListElement.parentElement ? pageListElement.parentElement.scrollTop : 0;
      movableElement.style.left = `${
        event.pageX - pageListElement.offsetLeft - movableElement.offsetWidth / 2
      }px`;
      movableElement.style.top = `${
        scrollTop + event.pageY - pageListElement.offsetTop - movableElement.offsetHeight / 2
      }px`;
      movableElement.style.visibility = "visible";
    },
    [pageListElement]
  );

  const displayDraggedPreview = (event: { pageX: number; pageY: number }) => {
    if (draggedPage && draggedPage.element) {
      setElementPosition(draggedPage.element, event);
    }
  };

  const onDragStartHandler = useCallback(
    (
      pageKey: string,
      movableElement: HTMLDivElement,
      refEvent: { pageX: number; pageY: number }
    ) => {
      if (!pageListElement) {
        return;
      }
      setElementPosition(movableElement, refEvent);
      setDraggedPage({ pageKey, element: movableElement });
      pageListElement.appendChild(movableElement);
    },
    [pageListElement, setDraggedPage, setElementPosition]
  );

  const undoKeyId = uuid();
  const onDragEndHandler = useCallback(
    (pageKey: string) => {
      if (draggedPage && draggedPage.element) {
        const movableElement = draggedPage.element;
        movableElement.style.transform = "scale(0.1)";
        setTimeout(() => {
          movableElement.remove();
          setDraggedPage(null);
          setPageTargetedByDrag(null);
        }, 250);
      }
      if (pageTargetedByDrag) {
        dispatch({
          pageKey,
          pageTarget: pageTargetedByDrag,
          type: "changePageOrder",
          undoKey: `changePageOrder-${undoKeyId}`,
        });
      }
    },
    [dispatch, draggedPage, pageTargetedByDrag, setDraggedPage, undoKeyId]
  );

  const onDragOverHandler = useCallback(
    (pageKey: string, position: "after" | "before") => {
      setPageTargetedByDrag({ pageKey, position });
    },
    [setPageTargetedByDrag]
  );

  const [selectedList, setSelectedList] = useState<string[]>([]);
  const onSelectItemHandler = useCallback(
    (pageKey: string) => {
      const position = selectedList.indexOf(pageKey);
      const newList = [...selectedList];
      if (position > -1) {
        newList.splice(position, 1);
      } else {
        newList.push(pageKey);
      }
      setSelectedList(newList);
      if (onItemSelected) {
        onItemSelected(newList);
      }
    },
    [onItemSelected, selectedList]
  );

  const listItems = useMemo(
    () =>
      map(contentPages, (page, pageIndex) => {
        const pageKey = page.key;

        const renderedPage = (
          <PagePresentation
            pageDefinitionKey={page.pageDefinitionKey}
            ppmm={ppmm}
            template={template}
          >
            <Page
              content={content}
              pageKey={pageKey}
              ppmm={ppmm}
              skipEvents={organizeMode === "deletion" || organizeMode === "ordering"}
              template={template}
            />
          </PagePresentation>
        );

        let pageTitle: string;
        if (configuration && configuration.pageName) {
          pageTitle = configuration.pageName.singular;
        } else if (
          template.templateType === "book" ||
          template.templateType === "mural" ||
          template.templateType === "print"
        ) {
          pageTitle = intl.formatMessage({
            id: `kustom_PageList_PageName__${template.templateType}`,
          });
        } else {
          const exhaustiveCheck: never = template;
          throw new Error(
            `Unsupported templateType "${
              (exhaustiveCheck as { templateType: string }).templateType
            }"`
          );
        }

        return {
          item: (
            <>
              <div className="EditablePageList-number">
                <span>
                  {pageTitle} {pageIndex + 1}
                </span>
              </div>
              {organizeMode === "ordering" ? (
                <div className="EditablePageList-renderedPage">
                  <DraggablePage
                    index={pageIndex}
                    onDragEnd={onDragEndHandler}
                    onDragOver={onDragOverHandler}
                    onDragStart={onDragStartHandler}
                    pageKey={pageKey}
                  >
                    {renderedPage}
                  </DraggablePage>
                </div>
              ) : (
                <div className="EditablePageList-renderedPage">
                  {organizeMode === "deletion" ? (
                    <RemovablePage
                      onSelectItem={() => {
                        onSelectItemHandler(pageKey);
                      }}
                      selected={selectedList.indexOf(pageKey) > -1}
                    >
                      {renderedPage}
                    </RemovablePage>
                  ) : (
                    { renderedPage }
                  )}
                </div>
              )}
            </>
          ),
          pageKey,
        };
      }),
    [
      configuration,
      content,
      contentPages,
      intl,
      onDragEndHandler,
      onDragOverHandler,
      onDragStartHandler,
      onSelectItemHandler,
      organizeMode,
      ppmm,
      selectedList,
      template,
    ]
  );

  const animatedListItems = compact(
    map(listItems, (listItem, pageIndex) => {
      if (template.templateType === "book" && template.pageMode === "single") {
        if (pageIndex > 0 && pageIndex % 2 !== 0) {
          return null;
        }

        // if (organizeMode === "ordering") {
        const nextPage = listItems[pageIndex + 1];
        const targetedPageKey = pageTargetedByDrag && pageTargetedByDrag.pageKey;
        const isDraggedFrom =
          organizeMode &&
          draggedPage &&
          (draggedPage.pageKey === listItem.pageKey || draggedPage.pageKey === nextPage.pageKey);
        let isDropTarget = false;
        let dropBarPosition: string | null = null;

        if (organizeMode && draggedPage && !isDraggedFrom && pageTargetedByDrag) {
          isDropTarget =
            targetedPageKey === listItem.pageKey || targetedPageKey === nextPage.pageKey;
          if (isDropTarget) {
            if (targetedPageKey === listItem.pageKey && pageTargetedByDrag.position === "before") {
              dropBarPosition = "before";
            }
            if (targetedPageKey === nextPage.pageKey && pageTargetedByDrag.position === "after") {
              dropBarPosition = "after";
            }
            if (
              (targetedPageKey === listItem.pageKey && pageTargetedByDrag.position === "after") ||
              (targetedPageKey === nextPage.pageKey && pageTargetedByDrag.position === "before")
            ) {
              dropBarPosition = "middle";
            }
          }
        }

        return (
          <motion.div
            animate={motionItemAnimate}
            exit={motionItemExit}
            initial={motionItemInitial}
            transition={motionItemSpring}
            key={listItem.pageKey}
            className={classNames("EditablePageList-item EditablePageList-item-double", {
              "EditablePageList-item-draggedFrom": isDraggedFrom,
              "EditablePageList-item-draggedOver": isDropTarget,
            })}
          >
            {dropBarPosition === "before" && (
              <div className="EditablePageList-item-organize-target">&nbsp;</div>
            )}
            <div
              className={classNames("EditablePageList-item-even", {
                "EditablePageList-item-dragged":
                  isDraggedFrom && draggedPage && listItem.pageKey === draggedPage.pageKey,
              })}
            >
              {listItem.item}
            </div>
            {dropBarPosition === "middle" && (
              <div className="EditablePageList-item-organize-target">&nbsp;</div>
            )}
            <div
              className={classNames("EditablePageList-item-odd", {
                "EditablePageList-item-dragged":
                  isDraggedFrom &&
                  draggedPage &&
                  listItems[pageIndex + 1].pageKey === draggedPage.pageKey,
              })}
            >
              {listItems[pageIndex + 1] && listItems[pageIndex + 1].item}
            </div>
            {dropBarPosition === "after" && (
              <div className="EditablePageList-item-organize-target">&nbsp;</div>
            )}
          </motion.div>
        );
      } else {
        return (
          <motion.div
            animate={motionItemAnimate}
            exit={motionItemExit}
            initial={motionItemInitial}
            transition={motionItemSpring}
            key={listItem.pageKey}
            className="EditablePageList-item"
          >
            {listItem.item}
          </motion.div>
        );
      }
    })
  );

  return (
    <div
      className="EditablePageList"
      onDragOver={organizeMode === "ordering" ? displayDraggedPreview : noop}
      ref={pageListRef}
    >
      <DndProvider backend={isTouchDevice() ? TouchBackend : HTML5Backend}>
        {animatedListItems}
      </DndProvider>
      {children}
    </div>
  );
};

export default EditablePageList;
